import React, { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import { useTranslation } from '@/hooks/useTranslation';
import { useMenuLabel } from '@/lib/menu-labels';
import { publicApi } from '@/lib/api';
import ComingSoonPage from '@/pages/ComingSoonPage';

/**
 * Guards the Dropshop routes with the admin's "Dropshop" menu switch.
 *
 * Switching the entry off used to only hide it from the navigation — the URL still served the
 * full page to anyone who had it bookmarked or was linked to it. Off now means closed.
 */
const DropshopGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const menuLabel = useMenuLabel();
  const dropshopOn = useAdminStore((s) => s.settings?.menuVisibility?.dropshop);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setState('loading');
    const refresh = async (): Promise<void> => {
      // A competing initialization request may supersede the first read; retry once.
      let ok = await useAdminStore.getState().refreshSettings();
      if (!ok && active) ok = await useAdminStore.getState().refreshSettings();
      if (!active) return;
      setState(ok ? 'ready' : 'error');
      if (ok && useAdminStore.getState().settings.menuVisibility?.dropshop !== false) {
        const collections = await publicApi.getDropshop().catch(() => null);
        if (active && collections) useAdminStore.setState({ dropshopCollections: collections as never });
      }
    };
    void refresh();
    const onFocus = (): void => { setState('loading'); void refresh(); };
    window.addEventListener('focus', onFocus);
    return () => { active = false; window.removeEventListener('focus', onFocus); };
  }, [attempt]);

  if (state === 'loading') return <div role="status" aria-label="Loading" className="min-h-screen bg-white dark:bg-black" />;
  if (state === 'error') return <div className="min-h-screen flex flex-col items-center justify-center gap-4"><p>Could not load this page.</p><button onClick={() => setAttempt(n => n + 1)}>Try again</button></div>;

  if (dropshopOn === false) {
    return (
      <ComingSoonPage
        label={menuLabel('dropshop', t.dropshop.title)}
        title={'Coming\nSoon'}
        subtitle={t.dropshop.comingSoonSubtitle}
        icon={Sparkles}
      />
    );
  }

  return <>{children}</>;
};

export default DropshopGate;
